import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { botKeys } from '../../lib/queryKeys';
import { toast } from 'sonner';
import {
  tasksApi, projectsApi, labelsApi, taskTypesApi, teamsApi,
  attachmentsApi, trashApi, taskStatusesApi,
} from '../../services/api';
import { useTeamRole, canEditInTeam, canCommentInTeam } from '../../hooks/useRole';
import { useDateTime } from '../../contexts/DateTimeContext';
import { useAuth } from '../../contexts/AuthContext';
import MentionPicker from '../unread/MentionPicker';
import { useCommentReadObserver } from '../../hooks/useCommentReadObserver';
import { useUnreadsQuery, useTaskBotStatusQuery, useTasksQuery, useTaskQuery, useProjectSectionsQuery } from '../../hooks/queries';
import ListView from '../ListView';
import {
  useMarkCommentsRead,
  useMarkCommentsUnread,
  useMarkTaskCommentsRead,
  useAssignTaskBot,
} from '../../hooks/mutations';
import { useScopeBots } from '../bots/botDisplay';
import BotAvatar from '../bots/BotAvatar';
import TaskBotPanel from '../bots/TaskBotPanel';
import BotReplying from '../bots/BotReplying';
import { botDisplayName, botTeamName, useBotLabel } from '../bots/botDisplay';
import MarkdownMessage from '../ai/MarkdownMessage';
import { useBots } from '../../contexts/BotsContext';
import { useUndo } from '../../contexts/UndoContext';
import { useTask } from '../../contexts/TaskContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import BlockNoteEditor from '../LazyBlockNoteEditor';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ProjectPicker from '../ui/ProjectPicker';
import { SpotlightMenuItems, spotlightReason, useSpotlightMarks } from '../spotlight/SpotlightMenu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from '../ui/dropdown-menu';
import AttachmentPreviewModal from '../ui/AttachmentPreviewModal';
import TaskNotesModal from '../TaskNotesModal';
import ConfirmModal from '../ConfirmModal';
import SeriesUpdateDialog from '../SeriesUpdateDialog';
import CompactPropertyRow from './CompactPropertyRow';
import AttachmentUploadButton from './AttachmentUploadButton';
import AskBotMenu from './AskBotMenu';
import { Switch } from '../ui/switch';

import DatePicker from './pickers/DatePicker';
import PriorityPicker from './pickers/PriorityPicker';
import DurationPicker from './pickers/DurationPicker';
import StatusPicker from './pickers/StatusPicker';
import LabelPicker from './pickers/LabelPicker';
import AssigneePicker from './pickers/AssigneePicker';
import TaskTypePicker from './pickers/TaskTypePicker';
import ReminderPicker from './pickers/ReminderPicker';
import RecurrencePicker from './pickers/RecurrencePicker';

import { PRIORITIES, extractMultilineText, descriptionToDoc } from '../../lib/constants';
import { printPage } from '../../native/print';

import {
  X, Calendar, Clock, Flag, Tag, Folder, CheckCircle2,
  Circle, Trash2, Send, MoreHorizontal, Bell,
  Paperclip, Copy, Printer,
  Link, Mail, CalendarX, CalendarDays, Inbox,
  Tags, Users, UserCircle, Loader2,
  Lock, LockOpen, RefreshCw,
  PanelRight, Maximize2, MapPin, Receipt,
  CheckCircle, WifiOff, CircleDot, MessageSquare, CornerLeftUp, ChevronRight, ArrowUpRight, Sparkles,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SERIES_PROPAGATE_FIELDS = new Set([
  'title', 'priority', 'recurrence', 'project_id', 'assigned_to', 'tags', 'estimated_duration',
]);

const DEBOUNCE_MS = 1500;

const priorityConfig = Object.fromEntries(
  Object.entries(PRIORITIES).map(([k, v]) => [k, { ...v, color: v.textColor, bg: v.bgLight }])
);

// ---------------------------------------------------------------------------
// UnifiedTaskDetail — visually mirrors UnifiedTaskCreation but with edit-mode
// per-field auto-save, comments, and the existing header actions.
// ---------------------------------------------------------------------------

const UnifiedTaskDetail = () => {
  const botLabel = useBotLabel();
  const { t } = useTranslation();
  const {
    isDetailOpen, activeTaskId, detailTeamId, closeTaskDetail, openTaskDetail,
    taskDetailViewMode, setTaskDetailViewMode, notifyTaskMutated, taskAction, takeTaskAction,
  } = useTask();
  const { formatDate, formatTime, formatDateTime, toLocalDateString } = useDateTime();
  const { user } = useAuth();
  const { pushUndo } = useUndo();
  const { limitOf } = useEntitlements();

  // ------ Unread comment indicators ------
  const { data: unreads } = useUnreadsQuery();
  const markCommentsRead = useMarkCommentsRead();
  const markCommentsUnread = useMarkCommentsUnread();
  const markTaskRead = useMarkTaskCommentsRead();

  // ------ Core state ------
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [labels, setLabels] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [customStatuses, setCustomStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  // ------ Save status: 'saved' | 'saving' | 'offline' ------
  const [saveStatus, setSaveStatus] = useState('saved');

  // ------ Comment state ------
  const [newComment, setNewComment] = useState('');
  const commentSubmittingRef = useRef(false); // synchronous guard against double-submit
  // Mention composer: null means the picker is closed. Anything else is the
  // text typed after the "@" that opened it.
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [mentionedBotIds, setMentionedBotIds] = useState([]);
  const commentInputRef = useRef(null);
  // BG_74: when a brand-new label is created it is also auto-assigned to this
  // task (LabelPicker fires onChange right after onCreateLabel). That assignment
  // would surface a generic "Labels changed" undo toast; set this flag so the
  // next label_ids update skips that toast in favour of the "Label created" one.
  const skipLabelUndoRef = useRef(false);

  // ------ Location draft: edited locally, persisted on blur/Enter so typing
  // doesn't fire a save + "Location changed" notification per keystroke. ------
  const [locationDraft, setLocationDraft] = useState('');

  // ------ Team state ------
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const teamRole = useTeamRole(teamData);
  // A locked task is read-only: fold the lock flag into canEdit so every
  // edit surface (title, description, location, complete checkbox, attachment
  // remove, AI edit actions) is disabled until the task is unlocked (BG_93).
  const isLocked = !!task?.is_locked;
  const canEdit = (!teamData || canEditInTeam(teamRole) || (teamRole === 'member')) && !isLocked;
  const canComment = !teamData || canCommentInTeam(teamRole);

  // Feedback shown when a mutation is attempted on a locked task.
  const notifyLocked = useCallback(() => {
    toast.error(t('tasks.task_locked_readonly', 'This task is locked. Unlock it to make changes.'));
  }, [t]);

  // ------ Attachments ------
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const previewAttachmentRef = useRef(null);

  // ------ Dialogs / menus ------
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [seriesPendingUpdate, setSeriesPendingUpdate] = useState(null);

  // ------ Refs ------
  const isOpenRef = useRef(isDetailOpen);
  isOpenRef.current = isDetailOpen;
  previewAttachmentRef.current = previewAttachment;
  const titleTimerRef = useRef(null);
  const descTimerRef = useRef(null);
  // The description's version (description_rev) the editor holds, and an edit not saved yet
  // ({ taskId, value }). A save says which version it was made on: one made on an older
  // version (a bot rewrote the description meanwhile) is refused, not saved over it, and the
  // open editor takes a newer version as soon as it is saved elsewhere.
  const descRevs = useRef(new Map());        // taskId -> description_rev the editor's text is on
  const descPendingRef = useRef(null);
  const descSavingRef = useRef(false);
  const descQueueRef = useRef(Promise.resolve());  // one save at a time, each on the version the last one made
  const descEditorRef = useRef(null);
  const [descConflict, setDescConflict] = useState(null);  // { taskId, mine, theirs, rev }

  // ------ Derived ------
  const taskId = activeTaskId;
  const taskIdRef = useRef(taskId);
  taskIdRef.current = taskId;
  const teamId = detailTeamId;
  const viewMode = taskDetailViewMode; // 'sidebar' | 'popup'

  const getTaskType = useCallback((tid) => taskTypes.find(tt => tt.id === tid), [taskTypes]);

  // Subtasks: this task's own (with a row to add more) and, for a subtask, its parent.
  const subtasksQuery = useTasksQuery({ parent_id: taskId }, { enabled: !!taskId && isDetailOpen });
  const parentQuery = useTaskQuery(task?.parent_id, { enabled: !!task?.parent_id && isDetailOpen });
  const selectedProject = task?.project_id ? projects.find(p => p.id === task.project_id) : null;
  // Where the task is, as links: its project (under a parent project) and list. A plain click
  // goes there (in the side panel the task stays open next to it); Ctrl/Cmd+click a new tab.
  const parentProject = selectedProject?.parent_id ? projects.find(p => p.id === selectedProject.parent_id) : null;
  const { data: projectSections } = useProjectSectionsQuery(isDetailOpen ? task?.project_id : null);
  const selectedSection = task?.section_id && Array.isArray(projectSections)
    ? projectSections.find(x => x.id === task.section_id && !x.deleted_at) : null;
  const navigate = useNavigate();
  const spotlight = useSpotlightMarks(isDetailOpen, true);
  const spotlightMark = task ? spotlight.items[task.id] : null;
  const projectHref = (projectId, sectionId) => `/app/projects?project=${projectId}${sectionId ? `&section=${sectionId}` : ''}`;
  const goToProject = (e, href) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;  // the browser's new tab
    e.preventDefault();
    const keepOpen = viewMode === 'sidebar' && window.matchMedia?.('(min-width: 768px)').matches;
    if (!keepOpen) closeTaskDetail();
    navigate(keepOpen ? `${href}&task=${taskId}` : href);
  };
  const crumbClass = 'text-sm truncate hover:text-foreground hover:underline underline-offset-2 cursor-pointer';
  const priority = priorityConfig[task?.priority] || priorityConfig.p4;

  // ========================================================================
  // Data loading
  // ========================================================================

  const loadTask = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const response = await tasksApi.getById(taskId);
      const taskData = response.data;
      descRevs.current.set(taskData.id, taskData.description_rev || 0);
      setTask(taskData);
      const effectiveTeamId = teamId || taskData.team_id || null;
      taskStatusesApi.getAll(effectiveTeamId).then(s => setCustomStatuses(s || [])).catch(() => {});
      setComments(taskData.comments || []);
    } catch (err) {
      console.error('Error loading task:', err);
    } finally {
      setLoading(false);
    }
  }, [taskId, teamId]);

  const loadData = useCallback(async () => {
    try {
      // NOTE: statuses are loaded in loadTask() using the task's OWN team
      // (effectiveTeamId = detailTeamId || task.team_id). Don't also fetch them
      // here with only detailTeamId - on personal surfaces (My Tasks) that is
      // null, which returns the user's personal statuses and raced with the
      // correct team statuses, so the same task showed different status sets.
      const [projectsRes, labelsRes, taskTypesRes] = await Promise.all([
        projectsApi.getAll(),
        labelsApi.getAll(),
        taskTypesApi.getAll(),
      ]);
      setProjects(projectsRes.data || []);
      setLabels(labelsRes.data || []);
      setTaskTypes(taskTypesRes.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  }, []);

  useEffect(() => {
    setShowDeleteConfirm(false);
    setShowContextMenu(false);
    setSeriesPendingUpdate(null);
    setPreviewAttachment(null);
    setNewComment('');
    setSaveStatus('saved');
    setDescConflict(null);
    clearTimeout(titleTimerRef.current);
    flushDescriptionRef.current?.();

    if (isDetailOpen && taskId) {
      loadTask();
      loadData();
    }
  }, [isDetailOpen, taskId]); // eslint-disable-line

  // Keep the location draft in sync with the loaded/saved value. Doesn't fire
  // while typing (task.location only changes on save), so it won't clobber the
  // in-progress edit.
  useEffect(() => {
    setLocationDraft(task?.location || '');
  }, [task?.location]);

  useEffect(() => {
    if (!task?.project_id || !projects.length) {
      setTeamMembers([]);
      setTeamData(null);
      return;
    }
    const project = projects.find(p => p.id === task.project_id);
    if (project?.team_id) {
      teamsApi.getById(project.team_id)
        .then(res => {
          setTeamMembers(res.data?.members || []);
          setTeamData(res.data || null);
        })
        .catch(() => { setTeamMembers([]); setTeamData(null); });
    } else {
      setTeamMembers([]);
      setTeamData(null);
    }
  }, [task?.project_id, projects]);

  // ========================================================================
  // Keyboard: Escape
  // ========================================================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (previewAttachmentRef.current) {
        setPreviewAttachment(null);
        return;
      }
      if (!isOpenRef.current) return;
      const inTextInput =
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable;
      if (!inTextInput) closeTaskDetail();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeTaskDetail]);

  const flushDescriptionRef = useRef(null);  // set below, where flushDescription is made
  useEffect(() => () => {
    clearTimeout(titleTimerRef.current);
    flushDescriptionRef.current?.();
  }, []);

  // ========================================================================
  // Field updates with per-field auto-save + status indicator
  // ========================================================================

  const handleUpdateField = useCallback(async (field, value) => {
    if (!taskId || !task) return;
    // Locked tasks are read-only — the only mutation allowed is unlocking
    // itself (field === 'is_locked'). Everything else gets a toast (BG_93).
    if (isLocked && field !== 'is_locked') { notifyLocked(); return; }
    const prevValue = task[field];
    const fieldLabels = {
      priority: t('tasks.field_priority_changed'),
      due_date: t('tasks.field_due_date_changed'),
      due_time: t('tasks.field_due_time_changed'),
      duration: t('tasks.field_duration_changed'),
      project_id: t('tasks.field_project_changed'),
      label_ids: t('tasks.field_labels_changed'),
      location: t('tasks.field_location_changed'),
      is_locked: value ? t('tasks.field_task_locked') : t('tasks.field_task_unlocked'),
      task_type_id: t('tasks.field_task_type_changed'),
      needs_invoice: t('tasks.field_invoice_changed'),
      assigned_to: t('tasks.field_assignees_changed'),
      reminders: t('tasks.field_reminders_changed'),
      recurrence: value ? t('tasks.field_recurrence_set') : t('tasks.field_recurrence_removed'),
      status: t('tasks.field_status_changed', 'Status changed'),
      status_id: t('tasks.field_status_changed', 'Status changed'),
    };

    if (task.recurrence && task.series_id && SERIES_PROPAGATE_FIELDS.has(field)) {
      setSeriesPendingUpdate({ field, value, prevValue, label: fieldLabels[field] || t('tasks.task_updated') });
      return;
    }

    setTask(prev => prev ? { ...prev, [field]: value } : prev);
    setSaveStatus('saving');
    // BG_74: consume the create-label flag so a brand-new label's auto-assignment
    // doesn't also raise a generic "Labels changed" undo toast.
    const skipUndo = field === 'label_ids' && skipLabelUndoRef.current;
    if (skipUndo) skipLabelUndoRef.current = false;
    try {
      await tasksApi.update(taskId, { [field]: value });
      setSaveStatus('saved');
      notifyTaskMutated();
      if (!skipUndo) {
        pushUndo({
          label: fieldLabels[field] || t('tasks.task_updated'),
          undo: async () => {
            await tasksApi.update(taskId, { [field]: prevValue });
            setTask(prev => prev ? { ...prev, [field]: prevValue } : prev);
            notifyTaskMutated();
          },
        });
      }
    } catch (err) {
      setTask(prev => prev ? { ...prev, [field]: prevValue } : prev);
      setSaveStatus('offline');
      console.error('Error updating field:', err);
    }
  }, [taskId, task, isLocked, notifyLocked, t, notifyTaskMutated, pushUndo]);

  // Status change — writes status + status_id in a single PATCH and pushes
  // a single undo entry. Previously these were two separate handleUpdateField
  // calls (one per field), producing two API calls and two stacked Undo
  // toasts where only the second one fully reverted the change.
  const handleStatusPick = useCallback(async ({ status, statusId }) => {
    if (!taskId || !task) return;
    if (isLocked) { notifyLocked(); return; }
    const prevStatus = task.status;
    const prevStatusId = task.status_id;
    if (status === prevStatus && statusId === prevStatusId) return;

    setTask(prev => prev ? { ...prev, status, status_id: statusId } : prev);
    setSaveStatus('saving');
    try {
      await tasksApi.update(taskId, { status, status_id: statusId });
      setSaveStatus('saved');
      notifyTaskMutated();
      pushUndo({
        label: t('tasks.field_status_changed', 'Status changed'),
        undo: async () => {
          await tasksApi.update(taskId, { status: prevStatus, status_id: prevStatusId });
          setTask(prev => prev ? { ...prev, status: prevStatus, status_id: prevStatusId } : prev);
          notifyTaskMutated();
        },
      });
    } catch (err) {
      setTask(prev => prev ? { ...prev, status: prevStatus, status_id: prevStatusId } : prev);
      setSaveStatus('offline');
      console.error('Error updating status:', err);
    }
  }, [taskId, task, isLocked, notifyLocked, t, notifyTaskMutated, pushUndo]);

  // Title — debounced auto-save
  const handleTitleChange = useCallback((value) => {
    setTask(prev => prev ? { ...prev, title: value } : prev);
    if (!taskId) return;
    clearTimeout(titleTimerRef.current);
    setSaveStatus('saving');
    const prevTitle = task?.title;
    titleTimerRef.current = setTimeout(async () => {
      try {
        await tasksApi.update(taskId, { title: value });
        setSaveStatus('saved');
        notifyTaskMutated();
        pushUndo({
          label: t('tasks.task_renamed', { name: value }),
          undo: async () => {
            await tasksApi.update(taskId, { title: prevTitle });
            setTask(prev => prev ? { ...prev, title: prevTitle } : prev);
            notifyTaskMutated();
          },
        });
      } catch {
        setSaveStatus('offline');
      }
    }, DEBOUNCE_MS);
  }, [taskId, task?.title, t, notifyTaskMutated, pushUndo]);

  // Description — debounced auto-save, on the version it was edited from. Saves go one after
  // another and each takes the version the previous one made (a save sent while the last one
  // was still on its way otherwise looked like someone else's change).
  const saveDescription = useCallback((forTaskId, value, title) => {
    const run = async () => {
      descSavingRef.current = true;
      const baseRev = descRevs.current.get(forTaskId) || 0;
      try {
        const res = await tasksApi.update(forTaskId, { description: value, description_base_rev: baseRev });
        descRevs.current.set(forTaskId, res.data?.description_rev ?? baseRev + 1);
        if (forTaskId === taskIdRef.current) setSaveStatus('saved');
        notifyTaskMutated();
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409 && detail?.error === 'description_changed') {
          // Changed meanwhile: neither version is lost, the person picks one; for a task
          // closed meanwhile they are told and can open it.
          if (forTaskId === taskIdRef.current) {
            setDescConflict({ taskId: forTaskId, mine: value, theirs: detail.description, rev: detail.description_rev });
            setSaveStatus('saved');
          } else {
            toast.error(t('tasks.description_not_saved', { title: title || t('common.untitled') }), {
              duration: 15000,
              action: { label: t('common.open'), onClick: () => openTaskDetail(forTaskId) },
            });
          }
        } else if (forTaskId === taskIdRef.current) {
          setSaveStatus('offline');
        }
      } finally {
        descSavingRef.current = false;
      }
    };
    descQueueRef.current = descQueueRef.current.then(run, run);
    return descQueueRef.current;
  }, [notifyTaskMutated, openTaskDetail, t]);

  // An edit typed just before the task closes or another opens is saved, not dropped.
  const flushDescription = useCallback(() => {
    const pending = descPendingRef.current;
    clearTimeout(descTimerRef.current);
    descTimerRef.current = null;
    descPendingRef.current = null;
    if (pending) saveDescription(pending.taskId, pending.value, pending.title);
  }, [saveDescription]);
  flushDescriptionRef.current = flushDescription;

  const handleDescriptionChange = useCallback((value) => {
    setTask(prev => prev ? { ...prev, description: value } : prev);
    if (!taskId) return;
    clearTimeout(descTimerRef.current);
    descPendingRef.current = { taskId, value, title: task?.title };
    setSaveStatus('saving');
    descTimerRef.current = setTimeout(() => {
      descTimerRef.current = null;
      const pending = descPendingRef.current;
      descPendingRef.current = null;
      if (pending) saveDescription(pending.taskId, pending.value, pending.title);
    }, DEBOUNCE_MS);
  }, [taskId, saveDescription, task?.title]);

  // The open task as the server has it (a bot's update_task, another person, another tab
  // refresh it through the live updates): a newer description replaces the one shown, unless
  // an edit of it is on its way (then the save finds out and asks).
  const liveTaskQuery = useTaskQuery(isDetailOpen ? taskId : null);
  const liveTask = liveTaskQuery.data;
  useEffect(() => {
    if (!liveTask || liveTask.id !== taskId || !task || task.id !== taskId) return;
    const rev = liveTask.description_rev || 0;
    if (rev <= (descRevs.current.get(taskId) || 0) || descPendingRef.current || descSavingRef.current) return;
    descRevs.current.set(taskId, rev);
    setTask(prev => (prev && prev.id === liveTask.id ? { ...prev, description: liveTask.description, description_rev: rev } : prev));
    descEditorRef.current?.setContent(descriptionToDoc(liveTask.description));
  }, [liveTask, taskId, task]);

  const keepMyDescription = useCallback(() => {
    const c = descConflict;
    setDescConflict(null);
    if (!c) return;
    descRevs.current.set(c.taskId, c.rev);
    setSaveStatus('saving');
    saveDescription(c.taskId, c.mine, task?.title);
  }, [descConflict, saveDescription, task?.title]);

  const takeNewDescription = useCallback(() => {
    const c = descConflict;
    setDescConflict(null);
    if (!c || c.taskId !== taskIdRef.current) return;
    descRevs.current.set(c.taskId, c.rev);
    setTask(prev => (prev ? { ...prev, description: c.theirs, description_rev: c.rev } : prev));
    descEditorRef.current?.setContent(descriptionToDoc(c.theirs));
  }, [descConflict]);

  // Mirrors isTaskCompleted in TasksTable
  const isTaskCompleted = useCallback((tk) => {
    if (tk?.status_id && customStatuses.length > 0) {
      const cs = customStatuses.find(s => s.id === tk.status_id);
      if (cs) return cs.category === 'completed';
    }
    return tk?.status === 'completed';
  }, [customStatuses]);

  // What the link from an e-mail asked for (TaskContext ?do=): mark the task done (with Undo,
  // as the check does) or start a reply. Once the task and its statuses are here.
  const statusesReady = !task?.status_id || customStatuses.length > 0;
  useEffect(() => {
    if (!task || task.id !== taskId || !taskAction || taskAction.taskId !== taskId || !statusesReady) return;
    const action = takeTaskAction(taskId);
    if (action === 'complete' && !isTaskCompleted(task)) handleToggleCompleteRef.current?.();
    if (action === 'reply') {
      setTimeout(() => {
        const el = commentInputRef.current;
        if (!el) return;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.focus();
      }, 300);
    }
  }, [task, taskId, taskAction, takeTaskAction, statusesReady, isTaskCompleted]);
  const handleToggleCompleteRef = useRef(null);

  const handleToggleComplete = async () => {
    if (!task) return;
    if (isLocked) { notifyLocked(); return; }
    const wasCompleted = isTaskCompleted(task);
    const actionLabel = wasCompleted
      ? t('tasks.marked_incomplete_undo', { name: task.title })
      : t('tasks.marked_complete_undo', { name: task.title });

    let update;
    let revert;
    if (task.status_id && customStatuses.length > 0) {
      const targetCategory = wasCompleted ? 'not_started' : 'completed';
      const targetStatus = customStatuses.find(s => s.category === targetCategory);
      if (targetStatus) {
        update = { status_id: targetStatus.id };
        revert = { status_id: task.status_id };
      } else {
        const newStatus = wasCompleted ? 'todo' : 'completed';
        update = { status: newStatus };
        revert = { status: task.status };
      }
    } else {
      const newStatus = wasCompleted ? 'todo' : 'completed';
      update = { status: newStatus };
      revert = { status: task.status };
    }

    setTask(prev => prev ? { ...prev, ...update } : prev);
    setSaveStatus('saving');
    try {
      await tasksApi.update(taskId, update);
      setSaveStatus('saved');
      notifyTaskMutated();
      pushUndo({
        label: actionLabel,
        undo: async () => {
          await tasksApi.update(taskId, revert);
          setTask(prev => prev ? { ...prev, ...revert } : prev);
          notifyTaskMutated();
        },
      });
    } catch {
      setTask(prev => prev ? { ...prev, ...revert } : prev);
      setSaveStatus('offline');
    }
  };
  handleToggleCompleteRef.current = handleToggleComplete;

  const handleSeriesJustThis = async () => {
    const { field, value, prevValue, label } = seriesPendingUpdate;
    setSeriesPendingUpdate(null);
    setTask(prev => prev ? { ...prev, [field]: value } : prev);
    setSaveStatus('saving');
    try {
      await tasksApi.update(taskId, { [field]: value, update_series: false });
      setSaveStatus('saved');
      notifyTaskMutated();
      pushUndo({
        label,
        undo: async () => {
          await tasksApi.update(taskId, { [field]: prevValue });
          setTask(prev => prev ? { ...prev, [field]: prevValue } : prev);
          notifyTaskMutated();
        },
      });
    } catch {
      setTask(prev => prev ? { ...prev, [field]: prevValue } : prev);
      setSaveStatus('offline');
    }
  };

  const handleSeriesAllFuture = async () => {
    const { field, value, prevValue, label } = seriesPendingUpdate;
    setSeriesPendingUpdate(null);
    setTask(prev => prev ? { ...prev, [field]: value } : prev);
    setSaveStatus('saving');
    try {
      await tasksApi.update(taskId, { [field]: value, update_series: true });
      setSaveStatus('saved');
      notifyTaskMutated();
      pushUndo({
        label: t('tasks.field_updated_series', { label }),
        undo: async () => {
          await tasksApi.update(taskId, { [field]: prevValue });
          setTask(prev => prev ? { ...prev, [field]: prevValue } : prev);
          notifyTaskMutated();
        },
      });
    } catch {
      setTask(prev => prev ? { ...prev, [field]: prevValue } : prev);
      setSaveStatus('offline');
    }
  };

  const handleUnschedule = async () => {
    if (!task) return;
    if (isLocked) { notifyLocked(); return; }
    const prevData = {
      scheduled_start: task.scheduled_start,
      scheduled_end: task.scheduled_end,
      is_split: task.is_split,
      scheduled_sessions: task.scheduled_sessions,
      total_sessions: task.total_sessions,
    };
    setSaveStatus('saving');
    try {
      await tasksApi.update(taskId, {
        scheduled_start: null, scheduled_end: null,
        is_split: false, scheduled_sessions: null, total_sessions: null,
      });
      setTask(prev => prev ? {
        ...prev,
        scheduled_start: null, scheduled_end: null,
        is_split: false, scheduled_sessions: null, total_sessions: null,
      } : prev);
      setSaveStatus('saved');
      notifyTaskMutated();
      pushUndo({
        label: t('tasks.task_unscheduled'),
        undo: async () => {
          await tasksApi.update(taskId, prevData);
          setTask(prev => prev ? { ...prev, ...prevData } : prev);
          notifyTaskMutated();
        },
      });
    } catch {
      setSaveStatus('offline');
    }
  };

  // ------ Comments ------
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    // Guard against double submission: Ctrl+Enter key-repeat (or a quick double
    // click on Send) fired this twice before `newComment` cleared, posting the
    // comment twice. A synchronous ref blocks the second call.
    if (commentSubmittingRef.current) return;
    commentSubmittingRef.current = true;
    try {
      // The server re-validates these against team membership, so a stale id
      // left over from an edited-away mention is harmless.
      const response = await tasksApi.addComment(taskId, newComment, mentionedUserIds, mentionedBotIds);
      setComments(prev => [response.data, ...prev]);
      setNewComment('');
      setMentionedUserIds([]);
      setMentionedBotIds([]);
      if (response.data?.mentioned_bot_ids?.length) syncBotRef.current?.({ statusOnly: true });
      if (response.data?.bots_without_credit?.length) toast.error(t('bots.credits.exhausted_mention'));
      setMentionQuery(null);
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      commentSubmittingRef.current = false;
    }
  };

  // The AI menu: a request to a bot, posted as a comment that mentions it. The bot
  // answers in a reply (BotReplying shows it working meanwhile).
  const askBot = async (bot, prompt) => {
    if (!bot || !taskId || commentSubmittingRef.current) return;
    commentSubmittingRef.current = true;
    try {
      const response = await tasksApi.addComment(taskId, `@${botDisplayName(bot, t)} ${prompt}`, [], [bot.id]);
      setComments(prev => [response.data, ...prev]);
      if (response.data?.bots_without_credit?.length) {
        toast.error(t('bots.credits.exhausted_mention'));
      } else {
        syncBotRef.current?.({ statusOnly: true });
        toast.success(t('bots.task_prompts.sent', { bot: botLabel(bot) }));
      }
    } catch (err) {
      // 428 (AI consent) and 402 (credits) open their own dialogs.
      if (![402, 428].includes(err?.response?.status)) toast.error(t('bots.chat.send_failed'));
    } finally {
      commentSubmittingRef.current = false;
    }
  };

  // Open the mention picker on an "@" run since the last whitespace, and keep
  // its query in sync as the user keeps typing.
  const handleCommentChange = (e) => {
    const value = e.target.value;
    setNewComment(value);
    const upToCaret = value.slice(0, e.target.selectionStart);
    const match = upToCaret.match(/@([^\s@]*)$/);
    setMentionQuery(match ? match[1] : null);
  };

  const handleMentionSelect = (member) => {
    const name = member.full_name || member.email;
    const el = commentInputRef.current;
    // Split at the caret so a mention typed mid-sentence replaces the "@run"
    // the user is actually editing, not the last one in the whole textarea.
    const caret = el ? el.selectionStart : newComment.length;
    const replaced = newComment.slice(0, caret).replace(/@([^\s@]*)$/, `@${name} `);
    const after = newComment.slice(caret);

    setNewComment(replaced + after);
    if (member.bot_id) {
      setMentionedBotIds(prev => prev.includes(member.bot_id) ? prev : [...prev, member.bot_id]);
    } else {
      setMentionedUserIds(prev =>
        prev.includes(member.user_id) ? prev : [...prev, member.user_id]
      );
    }
    setMentionQuery(null);

    // React rewrites the textarea value, which parks the caret at position 0.
    // Without this the next thing the user types lands at the START of the
    // comment ("please confirm.Hey @QA Bot"). Restore it after the paint.
    requestAnimationFrame(() => {
      const node = commentInputRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(replaced.length, replaced.length);
    });
  };

  // Only observe when this task actually has unread comments. The server is
  // the authority on which specific ones are unread; sending an id that is
  // already read is a harmless no-op delete, so the client can be generous
  // and simply exclude the user's own comments.
  const unreadCommentIds = useMemo(() => {
    if (!taskId || !unreads?.tasks?.[taskId]) return [];
    return comments.filter(c => c.user_id !== user?.id).map(c => c.id);
  }, [comments, unreads, taskId, user?.id]);

  const registerComment = useCommentReadObserver({
    unreadCommentIds,
    onRead: (ids) => markCommentsRead.mutate(ids),
  });

  const taskUnreadCount = (taskId && unreads?.tasks?.[taskId]?.comments) || 0;

  // Plain-name matching, not markup: the comment body stays plain text and
  // mentioned_user_ids is the authoritative list. Trade-off: renaming a user
  // stops the highlight matching in older comments. The indicator and the
  // notification are unaffected.
  const renderCommentBody = (comment) => {
    const text = extractMultilineText(comment.content);
    const names = [
      ...(comment.mentioned_user_ids || [])
        .map(id => teamMembers.find(m => m.user_id === id))
        .filter(Boolean)
        .map(m => m.full_name || m.email),
      ...(comment.mentioned_bot_ids || [])
        .map(id => scopeBots.find(b => b.id === id))
        .filter(Boolean)
        .map(b => botDisplayName(b, t)),
    ].filter(Boolean);
    if (names.length === 0 || typeof text !== 'string') return text;

    const escaped = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const parts = text.split(new RegExp(`(@(?:${escaped.join('|')}))`, 'g'));
    return parts.map((part, i) =>
      names.some(n => part === `@${n}`)
        ? <span key={i} className="text-blue-400 font-medium">{part}</span>
        : part
    );
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await tasksApi.deleteComment(commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  // ------ Attachments ------
  const handleAttachmentUpload = async (e) => {
    if (isLocked) { notifyLocked(); return; }
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    setUploadingAttachment(true);
    setSaveStatus('saving');
    // Upload everything in parallel — one bad file shouldn't block the rest,
    // so we use `allSettled` and only persist the successes. The single
    // task update at the end batches all attachments into one PUT.
    try {
      const results = await Promise.allSettled(files.map((f) => attachmentsApi.upload(f)));
      const uploaded = [];
      const failed = [];
      const overLimit = [];
      const tooLarge = []; // {name, limitMB}
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value?.data) {
          uploaded.push(r.value.data);
        } else if (r.reason?.response?.status === 402) {
          overLimit.push(files[i].name);
        } else if (r.reason?.response?.status === 413) {
          const limitBytes = r.reason.response.data?.detail?.limit_bytes;
          const limitMB = limitBytes ? Math.floor(limitBytes / (1024 * 1024)) : null;
          tooLarge.push({ name: files[i].name, limitMB });
        } else {
          failed.push(files[i].name);
        }
      });
      if (uploaded.length) {
        const updated = [...(task.attachments || []), ...uploaded];
        await tasksApi.update(taskId, { attachments: updated });
        setTask(prev => prev ? { ...prev, attachments: updated } : prev);
        setSaveStatus('saved');
        notifyTaskMutated();
      } else {
        setSaveStatus('saved');
      }
      if (overLimit.length) toast.error(t('tasks.storage_limit_reached'));
      if (tooLarge.length) {
        // Per-file message so the user knows which file was too big and what
        // the actual limit is for their plan.
        tooLarge.forEach(({ name, limitMB }) => {
          toast.error(`${name} ${t('tasks.attachment_too_large', { limit: limitMB ?? '?' })}`);
        });
      }
      if (failed.length) toast.error(`${t('tasks.file_upload_failed')}: ${failed.join(', ')}`);
    } catch (err) {
      console.error('Error uploading attachments:', err);
      toast.error(t('tasks.file_upload_failed'));
      setSaveStatus('offline');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleAttachmentRemove = async (att) => {
    if (isLocked) { notifyLocked(); return; }
    const updated = (task.attachments || []).filter(a => a.id !== att.id);
    const prevAttachments = task.attachments;
    setTask(prev => prev ? { ...prev, attachments: updated } : prev);
    setSaveStatus('saving');
    try {
      await tasksApi.update(taskId, { attachments: updated });
      await attachmentsApi.delete(att.id, att.object_key);
      setSaveStatus('saved');
      notifyTaskMutated();
    } catch {
      setTask(prev => prev ? { ...prev, attachments: prevAttachments } : prev);
      setSaveStatus('offline');
    }
  };

  // ------ Context menu actions ------
  const handleDeleteTask = async () => {
    const taskTitle = task?.title || t('tasks.untitled_task');
    try {
      await tasksApi.delete(taskId);
      closeTaskDetail();
      pushUndo({
        label: t('tasks.task_moved_to_trash', { name: taskTitle }),
        undo: async () => { await trashApi.restoreTask(taskId); },
        onUndone: () => notifyTaskMutated(),
      });
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const handleDuplicateTask = async () => {
    try {
      const res = await tasksApi.duplicate(taskId);
      setShowContextMenu(false);
      notifyTaskMutated();
      toast.success(t('tasks.task_duplicated', { name: task?.title }));
      pushUndo({
        label: t('tasks.task_duplicated', { name: task?.title }),
        undo: async () => { await tasksApi.delete(res.data.id); },
      });
    } catch {
      toast.error(t('tasks.task_duplicate_failed'));
    }
  };

  const handleCopyLink = () => {
    // Deep-link via the ?task= query param that TaskContext reads on any route.
    // /app/task/<id> is not a route and 404s when opened in a new tab.
    const taskUrl = `${window.location.origin}/app/tasks?task=${taskId}`;
    navigator.clipboard.writeText(taskUrl);
    setShowContextMenu(false);
    toast.success(t('common.copied', 'Copied to clipboard'));
  };

  const handlePrint = () => {
    setShowContextMenu(false);
    printPage().catch(() => {});  // the phone apps print through the system (native/print.js)
  };

  // ------ Label CRUD ------
  const handleCreateLabel = async (data) => {
    try {
      const res = await labelsApi.create(data);
      setLabels(prev => [...prev, res.data]);
      // Let other surfaces (e.g. the tasks table's Labels column) refresh their
      // own labels cache so a brand-new label renders instead of showing "-".
      window.dispatchEvent(new CustomEvent('labelsUpdated'));
      // BG_74: acknowledge the new label and suppress the redundant "Labels
      // changed" undo toast triggered by the auto-assignment that follows.
      skipLabelUndoRef.current = true;
      toast.success(t('tasks.label_created'));
      return res.data;
    } catch (err) {
      console.error('Error creating label:', err);
      return null;
    }
  };

  const handleDeleteLabel = async (labelId) => {
    try {
      await labelsApi.delete(labelId);
      setLabels(prev => prev.filter(l => l.id !== labelId));
    } catch (err) {
      console.error('Error deleting label:', err);
    }
  };

  const handleUpdateLabel = async (label, patch) => {
    try {
      await labelsApi.update(label.id, patch);
      setLabels(prev => prev.map(l => l.id === label.id ? { ...l, ...patch } : l));
      window.dispatchEvent(new CustomEvent('labelsUpdated'));
    } catch (err) {
      console.error('Error updating label:', err);
    }
  };

  // ========================================================================
  // Derived data for renders
  // ========================================================================
  const currentAssignees = useMemo(() => {
    if (!task) return [];
    return Array.isArray(task.assigned_to) ? task.assigned_to : task.assigned_to ? [task.assigned_to] : [];
  }, [task]);

  // Bots are offered in the assignee picker, next to people.
  const taskTeamId = task
    ? (task.team_id || projects.find((p) => p.id === task.project_id)?.team_id || null)
    : null;
  const scopeBots = useScopeBots(taskTeamId, task?.user_id);
  const { openBot: navigateToBot, openGallery } = useBots();
  const queryClient = useQueryClient();
  // Bots change the task on the server while they work (status, a reply or a
  // result comment); when their status moves on, pull those changes in.
  const { data: botStatus, dataUpdatedAt: botStatusAt } = useTaskBotStatusQuery(taskId, { enabled: !!taskId });
  const syncBotRef = useRef(null);
  const botStatusSeen = useRef(0);
  useEffect(() => {
    if (!botStatusAt) return;
    if (botStatusSeen.current) syncBotRef.current?.();
    botStatusSeen.current = botStatusAt;
  }, [botStatusAt]);
  const openBot = (botId) => { closeTaskDetail(); navigateToBot(botId, { projectId: task?.project_id }); };
  const assignTaskBot = useAssignTaskBot();
  const handleBotChange = async (botId) => {
    const previous = task?.assigned_bot_id ?? null;
    setTask((prev) => (prev ? { ...prev, assigned_bot_id: botId } : prev));
    try {
      await assignTaskBot.mutateAsync({ taskId: task.id, botId });
    } catch {
      setTask((prev) => (prev ? { ...prev, assigned_bot_id: previous } : prev));
      toast.error(t('tasks.task_save_failed'));
    }
  };

  // Owner = the task's creator (task.user_id). Resolve to a displayable
  // person via the current user or the team roster; fall back to the raw id.
  const owner = useMemo(() => {
    const ownerId = task?.user_id;
    if (!ownerId) return null;
    if (user && ownerId === user.id) {
      return { full_name: user.full_name, avatar_url: user.avatar_url, email: user.email };
    }
    const member = teamMembers.find(m => m.user_id === ownerId);
    return member || { full_name: null, avatar_url: null, email: null };
  }, [task?.user_id, user, teamMembers]);

  // ========================================================================
  // Sub-renders — visually mirror UnifiedTaskCreation layout
  // ========================================================================

  const renderSaveStatus = () => {
    if (saveStatus === 'saving') {
      return (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t('task_notes.saving')}
        </span>
      );
    }
    if (saveStatus === 'offline') {
      return (
        <span className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <WifiOff className="w-3.5 h-3.5" />
          {t('task_notes.offline')}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1.5 text-xs text-green-500">
        <CheckCircle className="w-3.5 h-3.5" />
        {t('task_notes.saved')}
      </span>
    );
  };

  // ----------- Header -----------
  const renderHeader = () => (
    <div className="flex items-center gap-3 px-5 py-3 border-b border-border dark:border-border flex-shrink-0">
      <div className="flex items-center gap-2 text-muted-foreground min-w-0">
        {selectedProject ? (
          <>
            <Folder className="w-4 h-4 flex-shrink-0" style={{ color: selectedProject.color }} />
            {/* Phones: the project only (the list is a tap away, next to the Project field) */}
            {parentProject && (
              <>
                <RouterLink to={projectHref(parentProject.id)} onClick={(e) => goToProject(e, projectHref(parentProject.id))}
                  className={`${crumbClass} max-w-[30%] max-md:hidden`} data-testid="task-parent-project-link">{parentProject.name}</RouterLink>
                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 max-md:hidden" />
              </>
            )}
            <RouterLink to={projectHref(selectedProject.id)} onClick={(e) => goToProject(e, projectHref(selectedProject.id))}
              className={crumbClass} data-testid="task-project-link">{selectedProject.name}</RouterLink>
            {selectedSection && (
              <>
                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 max-md:hidden" />
                <RouterLink to={projectHref(selectedProject.id, selectedSection.id)}
                  onClick={(e) => goToProject(e, projectHref(selectedProject.id, selectedSection.id))}
                  className={`${crumbClass} max-w-[40%] max-md:hidden`} data-testid="task-section-link">{selectedSection.name}</RouterLink>
              </>
            )}
          </>
        ) : (
          <>
            <Inbox className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <span className="text-sm">{t('tasks.no_project')}</span>
          </>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Save status */}
        <div className="mr-1">{renderSaveStatus()}</div>

        {/* View mode toggle - desktop only */}
        <button
          onClick={() => setTaskDetailViewMode(viewMode === 'sidebar' ? 'popup' : 'sidebar')}
          className="hidden md:flex p-1.5 hover:bg-muted dark:hover:bg-white/10 rounded-lg text-muted-foreground dark:text-muted-foreground hover:text-foreground dark:hover:text-foreground"
          title={viewMode === 'sidebar' ? t('tasks.switch_to_popup', 'Expand to popup') : t('tasks.switch_to_sidebar', 'Collapse to sidebar')}
        >
          {viewMode === 'sidebar' ? <Maximize2 className="w-4 h-4" /> : <PanelRight className="w-4 h-4" />}
        </button>

        {/* Lock */}
        {task && (
          <button
            onClick={(e) => { e.stopPropagation(); handleUpdateField('is_locked', !task.is_locked); }}
            title={task.is_locked ? t('tasks.unlock_description') : t('tasks.lock_description')}
            className={`p-1.5 rounded-lg transition-colors ${
              task.is_locked
                ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                : 'text-muted-foreground dark:text-muted-foreground hover:text-foreground dark:hover:text-foreground hover:bg-muted dark:hover:bg-white/10'
            }`}
          >
            {task.is_locked ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
          </button>
        )}

        {/* Ask a bot: the request goes to the bot as a comment that mentions it. */}
        <AskBotMenu bots={scopeBots} assignedBotId={task?.assigned_bot_id} onAsk={askBot}
          onAddBot={() => openGallery(taskTeamId ? { teamId: taskTeamId } : {})} disabled={!canComment} />

        {/* Context menu */}
        <div className="relative">
          <button
            onClick={() => setShowContextMenu(!showContextMenu)}
            className="p-1.5 hover:bg-muted dark:hover:bg-white/10 rounded-lg text-muted-foreground dark:text-muted-foreground hover:text-foreground dark:hover:text-foreground"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {showContextMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowContextMenu(false)} />
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-accent border border-border dark:border-border rounded-lg shadow-xl z-20 py-1">
                <button onClick={handleDuplicateTask} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-foreground/80 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/10">
                  <Copy className="w-4 h-4" />
                  <span className="text-sm">{t('common.duplicate')}</span>
                </button>
                <button onClick={handleCopyLink} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-foreground/80 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/10">
                  <Link className="w-4 h-4" />
                  <span className="text-sm">{t('tasks.copy_link_to_task')}</span>
                </button>
                <div className="border-t border-border dark:border-border my-1" />
                <button onClick={handlePrint} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-foreground/80 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/10">
                  <Printer className="w-4 h-4" />
                  <span className="text-sm">{t('common.print')}</span>
                </button>
                <div className="border-t border-border dark:border-border my-1" />
                <button onClick={() => { setShowDeleteConfirm(true); setShowContextMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-500 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm">{t('tasks.move_to_trash')}</span>
                </button>
              </div>
            </>
          )}
        </div>

        <button
          onClick={closeTaskDetail}
          className="p-1.5 hover:bg-muted dark:hover:bg-white/10 rounded-lg transition-colors"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );

  // ----------- Title (creation-style box with leading checkbox) -----------
  const renderTitleBox = () => (
    <div className="p-4 pb-2">
      <div className="flex items-center gap-2 bg-muted dark:bg-white/5 rounded-lg border border-border dark:border-border focus-within:border-amber-500/50 transition-colors p-1.5">
        <button
          onClick={handleToggleComplete}
          disabled={!canEdit}
          className="flex-shrink-0 ml-1"
          title={isTaskCompleted(task) ? t('tasks.mark_incomplete', 'Mark incomplete') : t('tasks.mark_complete', 'Mark complete')}
        >
          {isTaskCompleted(task) ? (
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          ) : (
            <Circle className={`w-5 h-5 ${priority.color} hover:opacity-80`} />
          )}
        </button>
        <input
          type="text"
          value={task?.title || ''}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder={t('tasks.task_input_placeholder')}
          disabled={!canEdit}
          className={`flex-1 bg-transparent text-sm placeholder:text-muted-foreground dark:placeholder:text-muted-foreground focus:outline-none px-2.5 py-1.5 ${
            isTaskCompleted(task) ? 'text-muted-foreground line-through' : 'text-foreground dark:text-foreground'
          }`}
          autoComplete="off"
        />
      </div>
    </div>
  );

  // ----------- Parent task (for a subtask) -----------
  const renderParentLink = () => {
    if (!task?.parent_id || !parentQuery.data) return null;
    return (
      <div className="px-4 pt-3 -mb-2">
        <button
          onClick={() => openTaskDetail(task.parent_id, { teamId })}
          className="flex items-center gap-1.5 max-w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
          title={t('tasks.parent_task')}
        >
          <CornerLeftUp className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{parentQuery.data.title}</span>
        </button>
      </div>
    );
  };

  // ----------- Subtasks: the list and a row to add one -----------
  const renderSubtasks = () => (
    <div className="px-4 pb-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 px-3 pb-1">
        {t('tasks.subtasks')}
      </div>
      <ListView
        tasks={subtasksQuery.data || []}
        projects={projects}
        teamId={teamId || task?.team_id || null}
        parentId={task.id}
        visibleFields={['priority', 'due_date']}
        readOnly={!canEdit}
      />
    </div>
  );

  // ----------- Description (creation-style always-on TipTap) -----------
  const renderDescriptionBox = () => {
    const desc = descriptionToDoc(task?.description);
    return (
      <div className="px-4 pb-4">
        <div className="bg-accent/60 rounded-lg border border-border p-3">
          <BlockNoteEditor
            ref={descEditorRef}
            content={desc}
            onChange={handleDescriptionChange}
            placeholder={t('tasks.description_placeholder')}
            minHeight="200px"
            className="text-sm"
            editable={canEdit}
            enableImageUpload={true}
            onImageUpload={(att) => {
              const updated = [...(task?.attachments || []), att];
              setTask(prev => prev ? { ...prev, attachments: updated } : prev);
              setSaveStatus('saving');
              tasksApi.update(taskId, { attachments: updated })
                .then(() => { setSaveStatus('saved'); notifyTaskMutated(); })
                .catch(() => setSaveStatus('offline'));
            }}
          />
        </div>
      </div>
    );
  };

  // ----------- Attachments list (left column under description) -----------
  const renderAttachmentsList = () => {
    if (!task?.attachments?.length) return null;
    return (
      <div className="px-4 pb-3 space-y-1">
        {task.attachments.map(att => (
          <div key={att.id} className="group flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-accent/60">
            {att.content_type?.startsWith('image/') ? (
              <img src={att.url} alt={att.filename} className="w-8 h-8 rounded object-cover flex-shrink-0 cursor-pointer" onClick={() => setPreviewAttachment(att)} />
            ) : (
              <Paperclip className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            )}
            <button
              onClick={() => setPreviewAttachment(att)}
              className="flex-1 text-sm text-foreground/80 hover:text-foreground truncate text-left"
            >
              {att.filename}
            </button>
            <span className="text-xs text-muted-foreground/70">{t('ui_task.attachments.size_kb', { size: Math.round(att.size / 1024) })}</span>
            {canEdit && (
              <button
                onClick={() => handleAttachmentRemove(att)}
                // Always visible: this is the only control to remove an attachment,
                // and a hover-only reveal left it invisible on touch devices.
                className="p-0.5 text-muted-foreground hover:text-red-400 transition-colors flex-shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ----------- Properties panel (creation-style) -----------
  const renderProperties = () => {
    if (!task) return null;
    const taskType = getTaskType(task.task_type_id);
    const needsInvoice = task.needs_invoice ?? taskType?.needs_invoice_default ?? false;

    return (
      <>
        {/* CORE */}
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 px-3 pt-3 pb-1">
          {t('tasks.core', 'Core')}
        </div>

        <CompactPropertyRow icon={CircleDot} label={t('tasks.status_label', 'Status')}>
          <StatusPicker
            value={task.status}
            statusId={task.status_id}
            customStatuses={customStatuses}
            onPick={handleStatusPick}
            disabled={isLocked}
          />
        </CompactPropertyRow>

        <CompactPropertyRow icon={Flag} label={t('tasks.priority', 'Priority')}>
          <PriorityPicker
            value={task.priority || 'p4'}
            onChange={(val) => handleUpdateField('priority', val)}
            disabled={isLocked}
          />
        </CompactPropertyRow>

        <CompactPropertyRow icon={Calendar} label={t('tasks.due_date', 'Due Date')}>
          <DatePicker
            value={task.due_date}
            onChange={(date) => handleUpdateField('due_date', date ? toLocalDateString(date) : null)}
            timeValue={task.due_time}
            onTimeChange={(val) => handleUpdateField('due_time', val)}
            disabled={isLocked}
          />
        </CompactPropertyRow>

        <CompactPropertyRow icon={Clock} label={t('tasks.duration', 'Duration')}>
          <DurationPicker
            value={task.duration || 60}
            onChange={(val) => handleUpdateField('duration', val)}
            disabled={isLocked}
          />
        </CompactPropertyRow>

        {/* DETAILS */}
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 px-3 pt-3 pb-1">
          {t('tasks.details', 'Details')}
        </div>

        <CompactPropertyRow icon={Tags} label={t('tasks.project', 'Project')}>
          <div className="flex items-center gap-1 min-w-0 max-md:justify-end">
            <ProjectPicker
              selectedProjectId={task.project_id}
              onSelect={(pid) => handleUpdateField('project_id', pid || '')}
              projects={projects}
              onProjectsChange={setProjects}
              disabled={isLocked}
            />
            {selectedProject && (
              <RouterLink to={projectHref(selectedProject.id, selectedSection?.id)}
                onClick={(e) => goToProject(e, projectHref(selectedProject.id, selectedSection?.id))}
                title={t('tasks.open_project')} aria-label={t('tasks.open_project')} data-testid="task-open-project"
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted dark:hover:bg-white/10 flex-shrink-0">
                <ArrowUpRight className="w-3.5 h-3.5" />
              </RouterLink>
            )}
          </div>
        </CompactPropertyRow>

        {/* Spotlight: the person's own short list (pages/app/SpotlightPage.jsx) */}
        <CompactPropertyRow icon={Sparkles} label={t('spotlight.title')}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="task-spotlight"
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] hover:bg-accent/60 transition-colors ${
                  spotlightMark ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>
                {spotlightMark
                  ? (spotlight.sections || []).find((x) => x.id === spotlightMark.section_id)?.name || t('spotlight.title')
                  : t('spotlight.not_in')}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {spotlightMark ? spotlightReason(t, spotlightMark.reason) : t('spotlight.add_to')}
              </DropdownMenuLabel>
              <SpotlightMenuItems taskId={task.id} current={spotlightMark} sections={spotlight.sections || []} />
            </DropdownMenuContent>
          </DropdownMenu>
        </CompactPropertyRow>

        <CompactPropertyRow icon={Users} label={t('tasks.assignee', 'Assignee')}>
          <AssigneePicker
            value={currentAssignees}
            teamMembers={teamMembers}
            onChange={(val) => handleUpdateField('assigned_to', val.length > 0 ? val : null)}
            bots={scopeBots}
            botId={task.assigned_bot_id || null}
            onBotChange={handleBotChange}
            disabled={isLocked}
          />
        </CompactPropertyRow>


        {owner && (
          <CompactPropertyRow icon={UserCircle} label={t('common.owner', 'Owner')}>
            <span className="flex items-center gap-2 min-w-0 max-md:justify-end">
              {owner.avatar_url ? (
                <img
                  src={owner.avatar_url}
                  alt=""
                  className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <span className="w-5 h-5 rounded-full bg-gray-300 dark:bg-white/10 flex items-center justify-center text-[10px] text-muted-foreground/70 dark:text-foreground/80 flex-shrink-0">
                  {owner.full_name?.[0]?.toUpperCase() || '?'}
                </span>
              )}
              <span className="truncate">
                {owner.full_name || owner.email || t('common.unknown', 'Unknown')}
              </span>
            </span>
          </CompactPropertyRow>
        )}

        <CompactPropertyRow icon={Tag} label={t('tasks.labels', 'Labels')}>
          <LabelPicker
            value={task.label_ids || []}
            labels={labels}
            onChange={(val) => handleUpdateField('label_ids', val)}
            onCreateLabel={handleCreateLabel}
            onDeleteLabel={handleDeleteLabel}
            onUpdateLabel={handleUpdateLabel}
            disabled={isLocked}
          />
        </CompactPropertyRow>

        <CompactPropertyRow icon={Tags} label={t('tasks.type', 'Type')}>
          <TaskTypePicker
            value={task.task_type_id}
            taskTypes={taskTypes}
            needsInvoice={needsInvoice}
            onChange={(val) => handleUpdateField('task_type_id', val)}
            onNeedsInvoiceChange={(val) => handleUpdateField('needs_invoice', val)}
            disabled={isLocked}
          />
        </CompactPropertyRow>

        {/* MORE */}
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 px-3 pt-3 pb-1">
          {t('tasks.more', 'More')}
        </div>

        <CompactPropertyRow icon={MapPin} label={t('common.location', 'Location')}>
          <input
            type="text"
            value={locationDraft}
            onChange={(e) => setLocationDraft(e.target.value)}
            onBlur={() => {
              if (locationDraft !== (task.location || '')) handleUpdateField('location', locationDraft);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            placeholder={t('tasks.add_location', 'Add location...')}
            disabled={!canEdit}
            className="w-full bg-transparent text-[13px] text-foreground/80 dark:text-foreground/80 placeholder:text-muted-foreground dark:placeholder:text-muted-foreground/70 focus:outline-none"
          />
        </CompactPropertyRow>

        <CompactPropertyRow icon={Receipt} label={t('tasks.billable', 'Billable')}>
          <Switch
            checked={needsInvoice}
            onCheckedChange={(val) => handleUpdateField('needs_invoice', val)}
            disabled={isLocked}
            aria-label={t('tasks.billable', 'Billable')}
          />
        </CompactPropertyRow>

        <CompactPropertyRow icon={Bell} label={t('tasks.reminders_label', 'Reminders')}>
          <ReminderPicker
            value={task.reminders || []}
            onChange={(val) => handleUpdateField('reminders', val)}
            disabled={isLocked}
            hasTime={!!(task.scheduled_start || (task.due_date && task.due_time))}
          />
        </CompactPropertyRow>

        <CompactPropertyRow icon={RefreshCw} label={t('tasks.repeat', 'Repeat')}>
          <RecurrencePicker
            value={task.recurrence || null}
            onChange={(val) => handleUpdateField('recurrence', val)}
            disabled={isLocked}
          />
        </CompactPropertyRow>

        {/* Attachment upload */}
        <CompactPropertyRow icon={Paperclip} label={t('tasks.attachments', 'Attachments')}>
          <AttachmentUploadButton
            onChange={handleAttachmentUpload}
            uploading={uploadingAttachment}
            count={task.attachments?.length || 0}
            disabled={isLocked}
          />
        </CompactPropertyRow>

        {/* Scheduled info banner with Unschedule button */}
        {task.scheduled_start && (
          <div className="mx-3 mt-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
              <CalendarDays className="w-4 h-4" />
              <span className="font-medium">{t('tasks.scheduled')}</span>
            </div>
            <div className="text-foreground text-sm mt-1">
              {formatDate(task.scheduled_start)} {formatTime(task.scheduled_start)}
              {task.scheduled_end && ` - ${formatTime(task.scheduled_end)}`}
            </div>
            <button
              onClick={handleUnschedule}
              className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-accent/60 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground text-sm"
            >
              <CalendarX className="w-4 h-4" />
              <span>{t('tasks.unschedule')}</span>
            </button>
          </div>
        )}

        {/* Completion History */}
        {task.completed_sessions_history?.length > 0 && (
          <div className="mx-3 mt-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-medium">{t('tasks.completed_work')}</span>
            </div>
            <div className="mt-2 space-y-2">
              {task.completed_sessions_history.map((h, idx) => (
                <div key={idx} className="text-xs text-zinc-400">
                  <div className="flex items-center justify-between">
                    <span>{t('tasks.min_session', { count: h.duration })}</span>
                    <span className="text-zinc-500">{formatDate(h.completed_at)}</span>
                  </div>
                  <div className="text-zinc-500">
                    {formatTime(h.start)} - {formatTime(h.end)}
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-green-500/20 text-sm text-green-400 font-medium">
                {t('tasks.total_minutes_completed', { count: task.completed_sessions_history.reduce((sum, h) => sum + (h.duration || 0), 0) })}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ----------- Email metadata -----------
  const renderEmailMetadata = () => {
    if (!task?.email_metadata) return null;
    return (
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-4 h-4 text-red-400" />
          <span className="text-sm font-medium text-foreground/80">{t('tasks.linked_email')}</span>
        </div>
        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('tasks.email_subject')}:</span>
            <span className="text-sm text-foreground truncate">{task.email_metadata.subject}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('tasks.email_from')}:</span>
            <span className="text-sm text-foreground/80">{task.email_metadata.from_email}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted-foreground">
              {t('tasks.messages_in_thread', { count: task.email_metadata.message_count || 1 })}
            </span>
            {task.email_metadata.is_unread && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">{t('tasks.email_unread')}</span>
            )}
          </div>
          {task.email_metadata.snippet && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.email_metadata.snippet}</p>
          )}
        </div>
      </div>
    );
  };

  // ----------- Comments -----------
  const assignedBot = task?.assigned_bot_id ? scopeBots.find((b) => b.id === task.assigned_bot_id) : null;
  // The bot changes the task on the server while it works; pull just the
  // fields it touches so edits in progress here are never overwritten.
  const syncBotChanges = async ({ statusOnly = false } = {}) => {
    if (statusOnly) {  // a bot was just mentioned: show that it is replying
      queryClient.invalidateQueries({ queryKey: botKeys.taskStatus(taskId) });
      return;
    }
    try {
      const { data: fresh } = await tasksApi.getById(taskId);
      setTask((prev) => prev && prev.id === fresh.id ? {
        ...prev, status: fresh.status, status_id: fresh.status_id, completed_at: fresh.completed_at,
        bot_review: fresh.bot_review,
      } : prev);
      if (fresh.comments) setComments(fresh.comments);
    } catch { /* the next open loads it */ }
  };
  syncBotRef.current = syncBotChanges;
  const botPanel = assignedBot ? <TaskBotPanel taskId={task.id} bot={assignedBot} /> : null;
  const renderComments = () => (
    <div className="px-4 pb-4 pt-3 border-t border-border dark:border-border/50">
      {canComment && (
        <div className="flex items-start gap-3 mb-4">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt={user.full_name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
              {user?.full_name?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 relative">
            {mentionQuery !== null && (teamMembers.length > 0 || scopeBots.length > 0) && (
              <MentionPicker
                members={teamMembers}
                bots={scopeBots.map((b) => ({ bot_id: b.id, full_name: botDisplayName(b, t), team: botTeamName(b), avatar_color: b.avatar_color }))}
                query={mentionQuery}
                onSelect={handleMentionSelect}
                onClose={() => setMentionQuery(null)}
              />
            )}
            <textarea
              ref={commentInputRef}
              value={newComment}
              onChange={handleCommentChange}
              placeholder={t('tasks.comment_placeholder')}
              rows={3}
              className="w-full bg-accent/60 border border-input rounded-lg px-3 py-2 text-foreground/80 text-sm focus:outline-none focus:border-input resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddComment();
              }}
            />
          </div>
          {newComment.trim() && (
            <button onClick={handleAddComment} className="p-2 bg-amber-500 rounded-lg text-black hover:bg-amber-400 flex-shrink-0">
              <Send className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
      <div className="space-y-4">
        {taskUnreadCount > 0 && (
          <button
            type="button"
            onClick={() => markTaskRead.mutate(taskId)}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline self-start"
          >
            {t('unread.mark_all_read')}
          </button>
        )}
        <BotReplying status={botStatus} bots={scopeBots} />
        {comments.map((comment) => (
          <div
            key={comment.id}
            ref={registerComment(comment.id)}
            className="flex items-start gap-3 group"
          >
            {comment.bot_id ? (
              <button type="button" onClick={() => openBot(comment.bot_id)} title={botLabel({ id: comment.bot_id, name: comment.user_name })}>
                <BotAvatar bot={{ id: comment.bot_id, name: comment.user_name, avatar_color: comment.bot_color }} size={32} />
              </button>
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                {comment.user_name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {comment.bot_id ? (
                  <button type="button" onClick={() => openBot(comment.bot_id)}
                    className="text-foreground text-sm font-medium hover:underline">
                    {botLabel({ id: comment.bot_id, name: comment.user_name })}
                  </button>
                ) : (
                  <span className="text-foreground text-sm font-medium">{comment.user_name}</span>
                )}
                {comment.bot_id && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide rounded px-1 py-px bg-amber-500/15 text-amber-500">
                    {t('bots.bot_badge')}
                  </span>
                )}
                <span className="text-muted-foreground text-xs">{formatDateTime(comment.created_at)}</span>
              </div>
              {comment.bot_id ? (
                <MarkdownMessage className="text-foreground/80 break-words">{comment.content}</MarkdownMessage>
              ) : (
                <p className="text-foreground/80 text-sm whitespace-pre-wrap break-words">
                  {renderCommentBody(comment)}
                </p>
              )}
            </div>
            {comment.user_id !== user?.id && (
              <button
                type="button"
                title={t('unread.mark_unread')}
                onClick={() => markCommentsUnread.mutate([comment.id])}
                className="opacity-0 group-hover:opacity-100 max-md:opacity-100 p-1 hover:bg-accent rounded text-muted-foreground hover:text-blue-400 flex-shrink-0"
              >
                <MessageSquare className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => handleDeleteComment(comment.id)} className="opacity-0 group-hover:opacity-100 max-md:opacity-100 p-1 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-400 flex-shrink-0">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  // ========================================================================
  // Locked banner — a locked task is read-only; surface that clearly and
  // offer a one-click unlock (BG_93).
  // ========================================================================
  const renderLockedBanner = () => {
    if (!isLocked) return null;
    return (
      <div className="mx-4 mt-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20 flex items-center gap-2.5 flex-shrink-0">
        <Lock className="w-4 h-4 text-amber-700 dark:text-amber-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-amber-700 dark:text-amber-400">{t('tasks.task_locked_title', 'Task locked')}</div>
          <div className="text-xs text-muted-foreground dark:text-muted-foreground">{t('tasks.task_locked_banner', 'This task is read-only. Unlock it to make changes.')}</div>
        </div>
        <button
          onClick={() => handleUpdateField('is_locked', false)}
          className="flex-shrink-0 px-2.5 py-1 rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-700 dark:text-amber-400 text-xs font-medium transition-colors"
        >
          {t('tasks.unlock', 'Unlock')}
        </button>
      </div>
    );
  };

  // ========================================================================
  // Layout: popup mode — mirrors UnifiedTaskCreation 80vw/90vh two-column
  // ========================================================================
  const renderPopupContent = () => (
    <>
      {renderHeader()}
      {renderLockedBanner()}
      <div className="flex-1 flex min-h-0 overflow-hidden max-md:flex-col max-md:overflow-y-auto">
        {loading || !task ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* LEFT / TOP: Title + Description + Attachments + Email + Comments */}
            <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-y-auto border-r border-border dark:border-border max-md:border-r-0 max-md:overflow-visible max-md:flex-none">
              {renderParentLink()}
              {renderTitleBox()}
              {botPanel}
              {renderDescriptionBox()}
              {renderAttachmentsList()}
              {renderSubtasks()}
              {renderEmailMetadata()}
              {renderComments()}
            </div>
            {/* RIGHT / BOTTOM: Properties */}
            <div className="w-[300px] flex-shrink-0 bg-muted dark:bg-card overflow-y-auto max-md:w-full max-md:flex-none max-md:border-t max-md:border-border dark:max-md:border-border">
              {renderProperties()}
            </div>
          </>
        )}
      </div>
    </>
  );

  // ========================================================================
  // Layout: sidebar mode — single column, 480px wide
  // ========================================================================
  const renderSidebarContent = () => (
    <>
      {renderHeader()}
      {renderLockedBanner()}
      <div className="flex-1 overflow-y-auto">
        {loading || !task ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div>
            {renderParentLink()}
            {renderTitleBox()}
            {botPanel}
            <div className="px-4 pb-4">
              <div className="bg-accent/60 rounded-lg border border-border p-3">
                <BlockNoteEditor
                  ref={descEditorRef}
                  content={descriptionToDoc(task.description)}
                  onChange={handleDescriptionChange}
                  placeholder={t('tasks.description_placeholder')}
                  minHeight="120px"
                  className="text-sm"
                  editable={canEdit}
                  enableImageUpload={true}
                  onImageUpload={(att) => {
                    const updated = [...(task?.attachments || []), att];
                    setTask(prev => prev ? { ...prev, attachments: updated } : prev);
                    setSaveStatus('saving');
                    tasksApi.update(taskId, { attachments: updated })
                      .then(() => { setSaveStatus('saved'); notifyTaskMutated(); })
                      .catch(() => setSaveStatus('offline'));
                  }}
                />
              </div>
            </div>
            {renderAttachmentsList()}
            {renderSubtasks()}
            <div className="border-t border-border dark:border-border">
              {renderProperties()}
            </div>
            {renderEmailMetadata()}
            {renderComments()}
          </div>
        )}
      </div>
    </>
  );

  // ========================================================================
  // Render
  // ========================================================================
  if (!isDetailOpen) return null;

  const modals = (
    <>
      <AttachmentPreviewModal
        attachment={previewAttachment}
        attachments={task?.attachments || []}
        onClose={() => setPreviewAttachment(null)}
        onNavigate={(att) => setPreviewAttachment(att)}
      />
      <ConfirmModal
        isOpen={!!descConflict}
        onClose={() => setDescConflict(null)}
        onConfirm={keepMyDescription}
        onCancel={takeNewDescription}
        title={t('tasks.description_conflict_title')}
        message={t('tasks.description_conflict_message')}
        confirmLabel={t('tasks.description_conflict_keep_mine')}
        cancelLabel={t('tasks.description_conflict_take_new')}
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteTask}
        title={t('tasks.move_to_trash_title')}
        message={t('tasks.move_to_trash_confirm', { days: limitOf('trash_retention_days') || 30 })}
        confirmLabel={t('tasks.move_to_trash')}
        danger
      />
      {showNotesModal && task && (
        <TaskNotesModal
          isOpen={showNotesModal}
          onClose={() => setShowNotesModal(false)}
          task={task}
          onNotesUpdate={(notes) => setTask(prev => prev ? { ...prev, notes } : prev)}
        />
      )}
      <SeriesUpdateDialog
        isOpen={!!seriesPendingUpdate}
        onJustThis={handleSeriesJustThis}
        onAllFuture={handleSeriesAllFuture}
        onCancel={() => setSeriesPendingUpdate(null)}
      />
    </>
  );

  // Sidebar mode
  if (viewMode === 'sidebar') {
    return (
      <>
        <SheetPrimitive.Root open={isDetailOpen} onOpenChange={(open) => { if (!open) closeTaskDetail(); }}>
          <SheetPrimitive.Portal>
            <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
            <SheetPrimitive.Content
              className="fixed z-50 bg-white dark:bg-popover shadow-2xl flex flex-col overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right right-0 top-0 bottom-0 w-[480px] max-w-[calc(100vw-48px)] border-l border-border dark:border-border max-md:inset-0 max-md:bottom-auto max-md:w-full max-md:max-w-none max-md:border-l-0 max-md:h-[var(--vvh,100dvh)] max-md:pt-[env(safe-area-inset-top)] max-md:pb-[env(safe-area-inset-bottom)]"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => { if (previewAttachmentRef.current) e.preventDefault(); }}
              onInteractOutside={(e) => { if (previewAttachmentRef.current) e.preventDefault(); }}
            >
              <SheetPrimitive.Title className="sr-only">
                {task?.title || t('tasks.task_detail', 'Task Detail')}
              </SheetPrimitive.Title>
              <SheetPrimitive.Description className="sr-only">
                {t('tasks.task_detail_description', 'View and edit task details')}
              </SheetPrimitive.Description>
              {renderSidebarContent()}
            </SheetPrimitive.Content>
          </SheetPrimitive.Portal>
        </SheetPrimitive.Root>
        {modals}
      </>
    );
  }

  // Popup mode — same shell as UnifiedTaskCreation
  return (
    <>
      <DialogPrimitive.Root open={isDetailOpen} onOpenChange={(open) => { if (!open) closeTaskDetail(); }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
          <DialogPrimitive.Content
            className="fixed inset-0 z-50 flex items-center justify-center p-4 max-md:p-0 max-md:bottom-auto max-md:h-[var(--vvh,100dvh)]"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => { if (previewAttachmentRef.current) e.preventDefault(); }}
            onInteractOutside={(e) => { if (previewAttachmentRef.current) e.preventDefault(); }}
          >
            <DialogPrimitive.Title className="sr-only">
              {task?.title || t('tasks.task_detail', 'Task Detail')}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              {t('tasks.task_detail_description', 'View and edit task details')}
            </DialogPrimitive.Description>
            <div className="w-[80vw] h-[90vh] max-w-[1400px] bg-white dark:bg-popover rounded-2xl border border-border dark:border-border shadow-2xl flex flex-col overflow-hidden max-md:w-full max-md:h-[var(--vvh,100dvh)] max-md:max-w-none max-md:rounded-none max-md:border-0 max-md:pt-[env(safe-area-inset-top)] max-md:pb-[env(safe-area-inset-bottom)]">
              {renderPopupContent()}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      {modals}
    </>
  );
};

export default UnifiedTaskDetail;
